/* @jsxImportSource react */
'use client';

import type { ComponentProps, ReactNode } from 'react';

import { Dialog } from '@base-ui/react/dialog';
import { Col, Row } from '@opendoor/atoms/box';
import { Button } from '@opendoor/atoms/button';
import { Text } from '@opendoor/atoms/text';
import { cn } from '@opendoor/atoms/utils';

import styles from './seller-address-confirmation.module.css';

type SellerAddressConfirmationProps = {
  open: boolean;
  container?: ComponentProps<typeof Dialog.Portal>['container'];
  onOpenChange: ComponentProps<typeof Dialog.Root>['onOpenChange'];
  onClose?: () => void;
  title: string;
  address?: { street: string; locality: string };
  onEdit: () => void;
  children?: ReactNode;
  error?: string;
  confirming: boolean;
  confirmDisabled: boolean;
  onConfirm: () => void;
  onOtherRelationship?: () => void;
  analyticsNames?: { edit: string; confirm: string; other: string };
};

export function SellerAddressConfirmation({
  open,
  container,
  onOpenChange,
  onClose,
  title,
  address,
  onEdit,
  children,
  error,
  confirming,
  confirmDisabled,
  onConfirm,
  onOtherRelationship,
  analyticsNames,
}: SellerAddressConfirmationProps) {
  const confirmLabel = onOtherRelationship ? 'Yes, I own this home. Continue.' : 'Confirm';

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal container={container}>
        <Dialog.Backdrop className={cn(styles.backdrop, container && styles.contained)} />
        <Dialog.Popup className={cn(styles.popup, container && styles.contained)}>
          <Row className={styles.header}>
            <Dialog.Title className={styles.title}>{title}</Dialog.Title>
            <Dialog.Close
              aria-label="Close address confirmation"
              onClick={onClose}
              render={
                <Button
                  aria-label="Close address confirmation"
                  analyticsName=""
                  variant="secondary"
                  className={styles.close}
                />
              }
            >
              <svg aria-hidden width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path
                  d="M6 6l12 12M18 6L6 18"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
              </svg>
            </Dialog.Close>
          </Row>
          {address && (
            <Row className={styles.address}>
              <Col className={styles.addressLines}>
                <Text as="p">{address.street}</Text>
                <Text as="p">{address.locality}</Text>
              </Col>
              <Button
                aria-label="Edit"
                analyticsName={analyticsNames?.edit ?? ''}
                variant="secondary"
                className={cn(styles.edit, '!text-od-content-accent-brand')}
                onClick={onEdit}
              >
                Edit
              </Button>
            </Row>
          )}
          {children}
          {error && (
            <Text as="p" role="alert" className={styles.error}>
              {error}
            </Text>
          )}
          <Col className={styles.actions}>
            <Button
              aria-label={confirming ? 'Confirming…' : confirmLabel}
              analyticsName={analyticsNames?.confirm ?? ''}
              variant="primary"
              className={styles.primary}
              disabled={confirming || confirmDisabled}
              aria-busy={confirming}
              onClick={onConfirm}
            >
              {confirming ? 'Confirming…' : confirmLabel}
            </Button>
            {onOtherRelationship && (
              <Button
                aria-label="I’m an agent or someone else"
                analyticsName={analyticsNames?.other ?? ''}
                variant="secondary"
                className={styles.secondary}
                disabled={confirming}
                onClick={onOtherRelationship}
              >
                I’m an agent or someone else
              </Button>
            )}
          </Col>
        </Dialog.Popup>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
